import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input, Modal, Button, Form, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Icon } from "../../../../components/icon/icon";
import ScenarioEditorWrapper from "./scenario-editor";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import debounce from "../../../../common/utils/debounce";
import { cacheFetcher } from "../../../../common/utils/cached-fetcher";
import TagsSelect from "../../../../components/tags-select/tags-select";
import axios from "axios";
import ApiLifeFacts from "../../../../api/life-facts";
import ApiArticles from "../../../../api/articles";
import { getAllActiveArticlePages } from "../utils/get-all-active-article-pages";
import ApiScenariosManagement from '../../../../api/scenarios-management';
import ApiOnboardingManagement from '../../../../api/onboarding-management';
import ApiTaskManagement from '../../../../api/task-managements';
import ApiProfileManagement from '../../../../api/profile-management';
import ApiSubjectsManagement from '../../../../api/subjects-manager';
import {
  FormSelect,
} from "../../../../components/form/form";
import ChangeLogsModalComponent from "../../../../components/modals/change-logs/change-logs-modal";
import ScenarioSimulatorQr from "../../../../components/scenario-simulator/scenario-simulator-qr";
import AssociationsBlock from "../../../../components/associations/associations-block";
import UnavailableArticlesNotice from "./unavailable-articles-notice";
import { resolveValidationIsValid, isPersistableGraph } from "../utils/draftValidity";
import { isBrokenReferencesError, errorMessageOf } from "../../../../common/utils/referenced-error";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const versionPayloadFrom = (version) => ({
  data: version?.data || null,
  isValid: version?.isValid !== false,
});

const versionPayloadKey = (payload) => payload ? JSON.stringify(payload) : null;
// LF360-1828: onboarding question types the behavior-rules editor's
// operator/value mapping supports. Anything else (image-choice, slider,
// photo-picker, ...) is filtered out of getOnboardingQuestions below.
const SUPPORTED_ONBOARDING_QUESTION_TYPES = new Set([
  'single',
  'multi',
  'input',
  'picker',
  'date',
  'time',
]);

const QrFlyout = ({ isOpen, anchorRef, onCancel, children }) => {
  if (!isOpen || !anchorRef?.current) return null;

  const rect = anchorRef.current.getBoundingClientRect();
  const navWidth = window.innerWidth <= 768 ? 48 : 64;
  const availableWidth = window.innerWidth - navWidth;
  const modalWidth = Math.min(392, availableWidth - 16);
  let left = rect.right - modalWidth - navWidth;
  left = Math.max(0, Math.min(left, availableWidth - modalWidth - 8));
  const top = rect.bottom + 8;

  return (
    <Modal
      title={"Scenario preview"}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      mask={false}
      closable
      getContainer={false}
      style={{
        position: "absolute",
        top,
        left,
        padding: 0,
      }}
      width={modalWidth}
    >
      {children}
    </Modal>
  );
};

const DiscardModalComponent = ({
  title,
  handleOk,
  handleCancel,
  isModalOpen,
}) => {
  return (
    <Modal
      title={title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Discard changes"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to discard changes?</p>
    </Modal>
  );
};

const normalizeGroups = (group) => {
  return group.map((cohort) => ({
    id: cohort.id,
    label: cohort.EnrollmentFieldOption.value,
  }));
};

const cachedGetAllArticles = cacheFetcher('scenario-editor:all-articles', async () => {
  const data = await Promise.all([
    getAllActiveArticlePages(),
    ApiArticles.getCategories(),
    ApiArticles.getTags(),
  ]);

  const articles = data[0]
    .filter((article) => article.isActive)
    .map((article) => ({
      id: article.id,
      text: article.title,
      itemUrl: {
        url: article.FactUrl?.url,
        source: article.FactUrl?.source,
        title: article.FactUrl?.title,
        id: article.FactUrl?.id,
      },
    }));
  const categories = data[1].data.categories.map((category) => ({
    id: category.id,
    name: category.name,
    description: category.description,
  }));
  const tags = data[2].data.tags.map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));

  return {
    data: {
      ids: articles,
      categories,
      tags: tags.length ? tags : [{ id: 0, name: "No tags" }],
    },
  };
});

const cachedGetAllFitFacts = cacheFetcher('scenario-editor:all-fit-facts', async () => {
  const data = await Promise.all([
    ApiLifeFacts.get({ status: 'active' }),
    ApiLifeFacts.getTags(),
  ]);

  const fitFacts = data[0].data.facts
    .filter((fact) => fact.isActive)
    .map((fact) => ({
      id: fact.id,
      text: fact.text,
      itemUrl: {
        url: fact.FactUrl?.url,
        source: fact.FactUrl?.source,
        title: fact.FactUrl?.title,
        id: fact.FactUrl?.id,
      },
    }));
  const categories = data[0].data.categories.map((category) => ({
    id: category.id,
    name: category.name,
    description: category.description,
  }));
  const tags = data[1].data.tags.map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));

  return {
    data: {
      ids: fitFacts,
      categories,
      tags: tags.length ? tags : [{ id: 0, name: "No tags" }],
    },
  };
});

const cachedGetAllScenario = cacheFetcher('scenario-editor:all-scenarios', async () => {
  // LF360-1938: `getScenarios()` is the paginated list endpoint - 20 per page,
  // and this call passes no page - so it only ever returned the first 20. The
  // editor needs every scenario to name the condition picker's question groups,
  // and any scenario past the first page fell back to showing its raw UUID.
  // `/simple` returns all of them as {id, title} and nothing else.
  const data = await ApiScenariosManagement.getScenariosSimple();
  const scenarios = data.data.scenarios.map((scenario) => ({
    id: scenario.id,
    name: scenario.title,
  }));

  return {
    data: scenarios,
  };
});

const ScenarioPage = ({
  scenario = {},
  toList,
  refreshScenario,
  openNotification,
  api,
  cohorts = [],
  isOnboarding,
  isSurveyTask = false,
  tasks,
  authors,
  updateScenarioAuthor,
  itemsConfigOverride,
  changeLogs
}) => {
  const [form] = Form.useForm();
  const [isCheckingChanges, setIsCheckingChanges] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isDraftValid, setIsDraftValid] = useState(
    scenario?.ScenarioVersion?.isValid !== false
  );
  const [isOpenChangeLogsModal, setOpenChangeLogsModal] = useState();
  const [localChangeLogs, setLocalChangeLogs] = useState(changeLogs || {});
  const [isChangesLoading, setIsChangesLoading] = useState(false);

  useEffect(() => {
    setLocalChangeLogs(changeLogs || {});
  }, [changeLogs]);

  useEffect(() => {
    Promise.allSettled([
      cachedGetAllArticles(),
      cachedGetAllFitFacts(),
      cachedGetAllScenario(),
    ]);
  }, []);

  const logsForScenario = (localChangeLogs && localChangeLogs[scenario?.id]) || [];

  const refreshScenarioChangesWithRetry = async ({
    attempts = 4,
    delay = 350,
  } = {}) => {
    let lastResult = null;

    for (let i = 0; i < attempts; i++) {
      if (i > 0) {
        await sleep(delay);
      }

      const refreshed = await refreshScenario?.();
      lastResult = refreshed;

      const nextChangeLogs =
        refreshed?.changeLogs ||
        refreshed?.data?.changeLogs ||
        null;

      const nextLogsForScenario =
        nextChangeLogs?.[scenario?.id] || [];

      if (nextChangeLogs) {
        setLocalChangeLogs(nextChangeLogs);
      }

      if (nextLogsForScenario.length > 0) {
        return refreshed;
      }
    }

    return lastResult;
  };

  const [previewActive, setPreviewActive] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null);
  const [isEditorProcessing, setIsEditorProcessing] = useState(false);
  const editorProcessingSeqRef = useRef(0);
  const pendingChangesPromisesRef = useRef(new Set());
  const scenarioVersionSaveTimerRef = useRef(null);
  const scenarioVersionSaveInFlightRef = useRef(false);
  const latestScenarioVersionPayloadRef = useRef(null);
  const persistedScenarioVersionPayloadRef = useRef(
    versionPayloadFrom(scenario?.ScenarioVersion)
  );
  const pendingEditorFinishRef = useRef(null);
  const showChangesButtons = !previewActive && (
    isDirty || scenario?.ScenarioVersion?.status === 'pending'
  );
  const showButtonsLoader =
    !previewActive &&
    isEditorProcessing &&
    !showChangesButtons;
  // const showButtonsLoader =
  //   !previewActive &&
  //   (isEditorProcessing || isChangesLoading);

  const openChangeLogsModal = () => {
    setOpenChangeLogsModal(true);
  };
  const playBtnRef = useRef(null);

  const handleToggleSimulator = () => {
    setShowSimulator((v) => !v);
  };

  const closeChangeLogsModal = () => {
    setOpenChangeLogsModal(false);
  };

  const handleChangeLogsCancel = () => {
    closeChangeLogsModal();
  };
  // const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const handlePreviewActive = async () => {
    try {
      if (!activeVersion) {
        let resp;
        let activeV;

        if (isSurveyTask) {
          resp = await ApiTaskManagement.getById(scenario.id, { status: 'active' });
          const versions = resp?.data?.task?.DailyTaskVersions ?? [];
          activeV = versions.find(v => v.status === 'active')
                || resp?.data?.task?.DailyTaskVersion
                || null;
        } else if (isOnboarding) {
          resp = await ApiOnboardingManagement.getScenarioById(
            scenario.id,
            { status: 'active' }
          );
          activeV = resp?.data?.onboardingSurvey?.OnboardingSurveyVersion || null;
        } else {
          resp = await ApiScenariosManagement.getScenarioById(
            scenario.id,
            { status: 'active' }
          );
          activeV = resp?.data?.scenario?.ScenarioVersion || null;
        }
        setActiveVersion(activeV);
      }
      // await delay(2000);
      setPreviewActive(true);
      closeChangeLogsModal();
    } finally {
    }
  };

  const handleReturnToDraft = () => {
    setPreviewActive(false);
  };

  const ScenarioEditModal = ({
    isOpen,
    onCancel,
    scenarioEditButtonRef,
    authors,
    scenario
  }) => {
    const [selectedAuthor, setSelectedAuthor] = useState(
      scenario.ScenarioVersion.userId
    );

    useEffect(() => {
      setSelectedAuthor(scenario.ScenarioVersion.userId);
    }, [scenario]);

    useEffect(() => {
    }, [scenario?.ScenarioVersion?.status]);

    const handleAuthorChange = (authorId) => {
      setSelectedAuthor(authorId);
      updateAuthor(authorId);
    };
    return (
      <Modal
        open={isOpen}
        onCancel={onCancel}
        footer={null}
        mask={false}
        getContainer={false}
        closable={false}
        style={{
          position: "absolute",
          top: (scenarioEditButtonRef?.current?.getBoundingClientRect().bottom ?? 0) + 5 + "px",
          left: Math.max(0, (scenarioEditButtonRef?.current?.getBoundingClientRect().left ?? 0) - (window.innerWidth <= 768 ? 48 : 64)) + "px",
        }}
      >
        <Form.Item
          name={"userId"}
          initialValue={selectedAuthor}
        >
          <span className="article-modal-tags-label">Author: </span>
          <FormSelect
            placeholder={"Select author"}
            options={authors?.map((author) => ({
              value: author.id,
              label: author.fullName,
            }))}
            value={authors.find((a) => a.id === selectedAuthor)?.id}
            onSelect={(authorId) => handleAuthorChange(authorId)}
            disabled={previewActive}
          />
        </Form.Item>
        {!isOnboarding && (
          <AssociationsBlock groups={scenario.associations || []} />
        )}
      </Modal>
    );
  };

  const updateAuthor = (authorId) => {
    form.setFieldValue("userId", authorId);
    setIsDirty(true);
    updateScenarioAuthor(scenario.ScenarioVersion.id, authorId);
  };

  const { t } = useTranslation();
  const [isOpenDiscardModal, setOpenDiscardModal] = useState(false);
  const [title, setTitle] = useState(scenario.title ?? "Test title");
  const [showSimulator, setShowSimulator] = useState(false);
  const [drawScenario, setDrawScenario] = useState();
  const [scenarioCohortsIds, setScenarioCohortsIds] = useState(
    scenario?.OnboardingSurveyVersion?.group
  );
  const [isLoading, setLoading] = useState();
  const [isOpenActivityModal, setOpenActivityModal] = useState(false);
  const scenarioEditButtonRef = useRef(null);
  const titleMeasureRef = useRef(null);
  const [titleInputWidth, setTitleInputWidth] = useState(140);

  const trackPendingChanges = (promise) => {
    const trackedPromise = Promise.resolve(promise)
      .catch(() => {})
      .finally(() => {
        pendingChangesPromisesRef.current.delete(trackedPromise);
      });

    pendingChangesPromisesRef.current.add(trackedPromise);
    return trackedPromise;
  };

  const waitForPendingChanges = async () => {
    const pendingChangesPromises = Array.from(pendingChangesPromisesRef.current);

    if (pendingChangesPromises.length) {
      await Promise.allSettled(pendingChangesPromises);
    }
  };

  const clearPendingScenarioVersionSave = () => {
    if (scenarioVersionSaveTimerRef.current) {
      clearTimeout(scenarioVersionSaveTimerRef.current);
      scenarioVersionSaveTimerRef.current = null;
    }
  };

  const flushScenarioVersionSave = async () => {
    clearPendingScenarioVersionSave();

    if (scenarioVersionSaveInFlightRef.current) {
      return;
    }

    const scenarioVersionId = scenario.ScenarioVersion?.id;
    const payload = latestScenarioVersionPayloadRef.current;

    if (
      !scenarioVersionId ||
      !payload ||
      versionPayloadKey(payload) === versionPayloadKey(persistedScenarioVersionPayloadRef.current)
    ) {
      pendingEditorFinishRef.current?.();
      pendingEditorFinishRef.current = null;
      setIsChangesLoading(false);
      return;
    }

    scenarioVersionSaveInFlightRef.current = true;
    setIsChangesLoading(true);

    const updatePromise = api
      .updateScenarioVersion(scenarioVersionId, payload)
      .then(async () => {
        if (versionPayloadKey(latestScenarioVersionPayloadRef.current) === versionPayloadKey(payload)) {
          persistedScenarioVersionPayloadRef.current = payload;
          await refreshScenarioChangesWithRetry({
            attempts: 2,
            delay: 500,
          });
        }
      })
      .finally(() => {
        scenarioVersionSaveInFlightRef.current = false;

        if (
          versionPayloadKey(latestScenarioVersionPayloadRef.current) !==
          versionPayloadKey(persistedScenarioVersionPayloadRef.current)
        ) {
          scenarioVersionSaveTimerRef.current = setTimeout(flushScenarioVersionSave, 0);
          return;
        }

        pendingEditorFinishRef.current?.();
        pendingEditorFinishRef.current = null;
        setIsChangesLoading(false);
      });

    trackPendingChanges(updatePromise);
    await updatePromise;
  };

  const scheduleScenarioVersionSave = () => {
    clearPendingScenarioVersionSave();
    scenarioVersionSaveTimerRef.current = setTimeout(flushScenarioVersionSave, 800);
  };

  const waitForScenarioVersionSaveIdle = async () => {
    for (let i = 0; i < 10; i++) {
      await flushScenarioVersionSave();
      await waitForPendingChanges();

      if (
        !scenarioVersionSaveInFlightRef.current &&
        (
          !latestScenarioVersionPayloadRef.current ||
          versionPayloadKey(latestScenarioVersionPayloadRef.current) ===
            versionPayloadKey(persistedScenarioVersionPayloadRef.current)
        )
      ) {
        return;
      }
    }
  };

  useEffect(() => {
    persistedScenarioVersionPayloadRef.current = versionPayloadFrom(scenario?.ScenarioVersion);
    latestScenarioVersionPayloadRef.current = null;
    setIsDraftValid(scenario?.ScenarioVersion?.isValid !== false);
    pendingEditorFinishRef.current = null;
    clearPendingScenarioVersionSave();
    setIsChangesLoading(false);

    return () => {
      clearPendingScenarioVersionSave();
    };
  }, [scenario?.ScenarioVersion?.id, scenario?.ScenarioVersion?.isValid]);

  const openActivityModal = () => {
    setOpenActivityModal(true);
  }

  const closeActivityModal = () => {
    setOpenActivityModal(false);
    refreshScenario()
  };

  const onBack = async () => {
    console.log(isLoading);
    await waitForScenarioVersionSaveIdle();
    await toList();
  };

  const updateScenarioRef = useRef(null);
  const lastSavedTitleRef = useRef(scenario.title);

  const updateScenario = (key, value) => {
    if (key === "title" && value === lastSavedTitleRef.current) {
      return;
    }

    setLoading(true);
    const updatePromise = api
      .updateScenario(scenario.id, { [key]: value })
      .then(async (response) => {
        if (key === "title") {
          lastSavedTitleRef.current = response?.data?.scenario?.title ?? value;
          await refreshScenario?.();
          return;
        }

        await refreshScenario?.();
      })
      .catch((error) => {
        const responseError = error?.response?.data?.error;
        const errorCode =
          responseError?.code ||
          responseError ||
          error?.response?.data?.code;
        const isDuplicateTitleError =
          errorCode === "already_exist" ||
          errorCode === "SCENARIO_TITLE_EXISTS";

        openNotification({
          type: "error",
          message: (
            <p>
              {isDuplicateTitleError
                ? "A scenario with this title already exists. Enter a different title."
                : t("administration.scenario.notification.not_implemented", {
                    scenario: scenario.value,
                  })}
            </p>
          ),
        });

        if (key === "title") {
          setTitle(scenario.title ?? "");
        }
      })
      .finally(() => {
        setLoading(false);
      });

    trackPendingChanges(updatePromise);
  };

  updateScenarioRef.current = updateScenario;

  const debouncedUpdateScenarioTitle = useMemo(
    () => debounce((value) => updateScenarioRef.current?.("title", value), 800),
    [scenario.id ? String(scenario.id) : undefined]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateScenarioTitle.cancel?.();
    };
  }, [debouncedUpdateScenarioTitle]);

  useEffect(() => {
    setTitle(scenario.title ?? "");
    lastSavedTitleRef.current = scenario.title;
  }, [scenario.id]);

  useEffect(() => {
    if (title !== undefined && title !== scenario.title && title !== lastSavedTitleRef.current && title !== "") {
      debouncedUpdateScenarioTitle(title);
    }
  }, [title, scenario.title, debouncedUpdateScenarioTitle]);

  const updateScenarioTitle = (e) => {
    setTitle(e.target.value);
  };

  useEffect(() => {
    if (!titleMeasureRef.current) return;

    setTitleInputWidth(
      Math.min(480, Math.max(140, titleMeasureRef.current.offsetWidth + 24))
    );
  }, [title]);

  const discardScenario = async () => {
    setIsCheckingChanges(true);
    setLoading(true);
    // Let an in-flight autosave settle first so it can't resurrect the draft
    // right after we discard it — but a failing autosave must NOT block the
    // discard itself (that is the whole point of LF360-1811): swallow the error
    // and drop any pending save before continuing.
    try {
      await waitForScenarioVersionSaveIdle();
    } catch (error) {
      console.warn('[scenario-page] autosave did not settle before discard', error);
    }
    clearPendingScenarioVersionSave();
    latestScenarioVersionPayloadRef.current = persistedScenarioVersionPayloadRef.current;
    pendingEditorFinishRef.current = null;
    api
      .discardScenarioChanges(scenario.ScenarioVersion.id)
      .then(() => {
        openNotification({
          type: "success",
          message: <p>{t("scenario_management.notification.discard")}</p>,
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t("scenario_management.notification.discard_error")}</p>,
        });
      })
      .finally(async () => {
        setLoading(false);
        await refreshScenario();
        setIsDirty(false);
        setIsCheckingChanges(false);
        toList();
      });
  };

  const activateScenario = async () => {
    if (!isDraftValid) {
      return;
    }
    await waitForScenarioVersionSaveIdle();
    setIsCheckingChanges(true);
    setLoading(true);

    api
      .activateScenario(scenario.ScenarioVersion.id)
      .then(() => {
        openNotification({
          type: "success",
          message: <p data-testid="scenario-activated">{t("scenario_management.notification.activated")}</p>,
        });

        if (isOnboarding && !scenarioCohortsIds?.length) {
          openNotification({
            type: "info",
            message: (
              <p>{t("scenario_management.notification.cohort_not_assigned")}</p>
            ),
          });
        }
      })
      .catch((e) => {
        console.log("activateScenario error: ", e);
        // Publishing a flow that references a missing object is blocked by the
        // backend (LF360-1818) — surface its specific message.
        const brokenMsg = isBrokenReferencesError(e) ? errorMessageOf(e) : null;
        openNotification({
          type: "error",
          message: (
            <p>{brokenMsg || t("scenario_management.notification.activated_error")}</p>
          ),
        });
      })
      .finally(async () => {
        setDrawScenario();
        await refreshScenario();
        setLoading(false);
        setIsDirty(false);
        setIsCheckingChanges(false);
        setActiveVersion(null);
      });
  };

  const getAlternativeMessages = useCallback((
    originalMessage,
    alreadyGeneratedMessages
  ) => {
    if (api.getAlternativeMessages) {
      return api.getAlternativeMessages(
        originalMessage,
        alreadyGeneratedMessages
      );
    } else {
      return axios.post(`api/v1/scenario-managements/alternative-messages`, {
        originalMessage,
        alreadyGeneratedMessages,
      });
    }
  }, [api]);

  const getAllMessages = useCallback(() => {
    return api.getAllMessages();
  }, [api]);

  const getOptionsByMessage = useCallback((message) => {
    return api.getOptionsByMessage(message);
  }, [api]);

  const getAllFitFacts = useCallback(cachedGetAllFitFacts, []);

  const getAllArticles = useCallback(cachedGetAllArticles, []);

  const getFilteredFitFacts = useCallback(async (tags, categories) => {
    const data = await ApiLifeFacts.get({
      tags,
      categoryIds: categories,
      status: 'active'
    });
    const fitFacts = data.data.facts.map((fact) => ({
      id: fact.id,
      text: fact.text,
      itemUrl: {
        url: fact.FactUrl?.url,
        source: fact.FactUrl?.source,
        title: fact.FactUrl?.title,
        id: fact.FactUrl?.id,
      },
    }));
    const appliedFilters = {
      tags,
      categories,
    };

    return {
      data: {
        ids: fitFacts,
        appliedFilters,
      },
    };
  }, []);

  const getFilteredArticles = useCallback(async (tag, categories) => {
    const pages = await getAllActiveArticlePages({
      tag,
      categoryIds: categories,
    });
    const articles = pages.map((article) => ({
      id: article.id,
      text: article.text,
      itemUrl: {
        url: article.FactUrl?.url,
        source: article.FactUrl?.source,
        title: article.FactUrl?.title,
        id: article.FactUrl?.id,
      },
    }));
    const appliedFilters = {
      tags: tag,
      categories,
    };

    return {
      data: {
        ids: articles,
        appliedFilters,
      },
    };
  }, []);

  const getAllScenario = useCallback(cachedGetAllScenario, []);

  const getTaskManagements = useCallback(async () => {
    const data = await ApiTaskManagement.getTasks();
    return {
      data: data.data.taskManagements.filter(x => x.isActive),
    }
  }, []);

  // LF360-1828: project-wide, per-field fetcher for the behavior-rules editor's
  // "profile field" condition source. Unlike the old scenario-scoped version,
  // this returns ONE row per subject profile field (not per boundTo binding),
  // keyed by the field's own id so `criterion.profileFieldId` resolves in the
  // backend evaluator (see lf360-be profileManagement.js resolveProfileFieldValue).
  const getProfileManagement = useCallback(async () => {
    const data = await ApiProfileManagement.getProfiles();

    const mapped = data.data.subjectProfileFields.map((field) => ({
      sourceId: field.id,
      message: field.title,
      nodeType: field.inputType || '',
      type: 'profileField',
      nodeId: field.id,
    }));

    return {
      data: mapped,
    };
  }, []);

  // LF360-1828: project-wide onboarding-question fetcher for the behavior-rules
  // editor's "onboarding question" condition source. Filters out question types
  // the editor's operator/value mapping doesn't support (image-choice, slider,
  // photo-picker, ...).
  const getOnboardingQuestions = useCallback(async () => {
    const data = await ApiOnboardingManagement.getQuestions();

    const mapped = data.data.questions
      .map((question) => ({
        id: question.id,
        title: question.title,
        inputType: (question.questionType || '').replace(/^message:/, ''),
        options: question.options ?? [],
      }))
      .filter((question) => SUPPORTED_ONBOARDING_QUESTION_TYPES.has(question.inputType));

    return {
      data: mapped,
    };
  }, []);

  const fetchImageUrlById = useCallback((imageIds, size, withCrop = true) => {
    if (imageIds.length) {
      return ApiSubjectsManagement.getImageUrls({ imageIds, size, withCrop });
    }
    return Promise.resolve([]);
  }, []);

  const fetchImageContentById = useCallback((imageIds, size, withCrop = true) => {
    if (imageIds.length) {
      return ApiSubjectsManagement.getImageContent({ imageIds, size, withCrop });
    }
    return Promise.resolve([]);
  }, []);

  const uploadImage = useCallback((params) => {
    return ApiSubjectsManagement.uploadImageChoice(params);
  }, []);

  const fetchers = useMemo(() => ({
    getAlternativeMessages,
    getAllMessages,
    getOptionsByMessage,
    getAllFitFacts,
    getAllArticles,
    getFilteredFitFacts,
    getFilteredArticles,
    getAllScenario,
    getTaskManagements,
    getProfileManagement,
    getOnboardingQuestions,
    fetchImageUrlById,
    uploadImage,
    fetchImageContentById,
  }), [
    getAlternativeMessages,
    getAllMessages,
    getOptionsByMessage,
    getAllFitFacts,
    getAllArticles,
    getFilteredFitFacts,
    getFilteredArticles,
    getAllScenario,
    getTaskManagements,
    getProfileManagement,
    getOnboardingQuestions,
    fetchImageUrlById,
    uploadImage,
    fetchImageContentById,
  ]);

  const openDiscardModal = () => {
    setOpenDiscardModal(true);
  };

  const closeDiscardModal = () => {
    setOpenDiscardModal(false);
  };

  const handleDiscardOk = () => {
    discardScenario();
    closeDiscardModal();
  };

  const handleDiscardCancel = () => {
    closeDiscardModal();
  };

  const updateDrawScenario = useCallback((editedScenario, validation) => {
    const currentSeq = ++editorProcessingSeqRef.current;
    const startedAt = Date.now();
    const MIN_SPINNER_TIME = 400;

    setIsEditorProcessing(true);

    const finishEditorProcessing = () => {
      const elapsed = Date.now() - startedAt;
      const rest = Math.max(0, MIN_SPINNER_TIME - elapsed);

      setTimeout(() => {
        if (editorProcessingSeqRef.current === currentSeq) {
          setIsEditorProcessing(false);
        }
      }, rest);
    };

    // A genuine "empty" scenario still has Start + Finish nodes, so `{}` is a
    // transient/error emission from the editor — never persist it over a draft.
    if (!isPersistableGraph(editedScenario)) {
      finishEditorProcessing();
      return;
    }

    // Editor sends an array of errors, the AI chatbot sends { isValid, errors }.
    // Unknown shape => fail closed: don't persist and don't touch validity,
    // rather than marking an unverified graph valid (or every chatbot edit invalid).
    const isValid = resolveValidationIsValid(validation);
    if (isValid === null) {
      console.warn('[scenario-page] unrecognised validation payload, skipping save', validation);
      finishEditorProcessing();
      return;
    }

    const payload = {
      data: JSON.stringify(editedScenario),
      isValid,
    };
    const hasChanges =
      versionPayloadKey(payload) !==
      versionPayloadKey(persistedScenarioVersionPayloadRef.current);

    setDrawScenario(editedScenario);
    setIsDraftValid(isValid);

    if (!hasChanges || !scenario.ScenarioVersion?.id) {
      finishEditorProcessing();
      return;
    }

    setIsDirty(true);
    setIsChangesLoading(true);
    latestScenarioVersionPayloadRef.current = payload;
    pendingEditorFinishRef.current = finishEditorProcessing;
    scheduleScenarioVersionSave();
  }, [scenario.ScenarioVersion?.id]);

  const updateScenarioCohorts = (value) => {
    if (isOnboarding) {
      const valuesIds = value.map((v) => v.id);
      setScenarioCohortsIds(valuesIds);
      setIsChangesLoading(true);

      const updatePromise = api
        .updateScenarioVersion(scenario.ScenarioVersion?.id, {
          group: valuesIds,
        })
        .then(async () => {
          await refreshScenarioChangesWithRetry({
            attempts: 5,
            delay: 400,
          });
        })
        .finally(() => {
          setIsChangesLoading(false);
        });

      trackPendingChanges(updatePromise);
    }
  };

  const safeParse = (json) => {
    try { return json ? JSON.parse(json) : json; } catch { return json; }
  };

  const draftData = useMemo(
    () => safeParse(scenario.ScenarioVersion?.data),
    [scenario.ScenarioVersion?.data]
  );
  const activeData = useMemo(
    () => safeParse(activeVersion?.data),
    [activeVersion?.data]
  );

  const editorData = previewActive ? activeData : (drawScenario ?? draftData);

  const editorKey = previewActive
    ? `active-${activeVersion?.id || 'none'}`
    : `draft-${scenario.ScenarioVersion?.id || 'none'}`;

  const scenarioEditor = useMemo(() => (
    <ScenarioEditorWrapper
      key={editorKey}
      id={previewActive ? activeVersion?.id : scenario.ScenarioVersion?.id}
      scenarioId={isSurveyTask ? scenario.ScenarioVersion?.dailyTaskId : scenario.ScenarioVersion?.scenarioId}
      scenarioTitle={scenario.title}
      scenario={editorData}
      update={previewActive ? undefined : updateDrawScenario}
      scenarioFacts={scenario.facts}
      tasks={tasks}
      fetchers={fetchers}
      itemType={isSurveyTask ? 'surveyTask' : isOnboarding ? 'onboarding' : 'scenario'}
      itemsConfigOverride={itemsConfigOverride}
      isPreview={previewActive}
      isOnboarding={isOnboarding}
    />
  ), [
    editorKey,
    previewActive,
    activeVersion?.id,
    scenario.ScenarioVersion?.id,
    isSurveyTask,
    scenario.ScenarioVersion?.dailyTaskId,
    scenario.ScenarioVersion?.scenarioId,
    scenario.title,
    editorData,
    updateDrawScenario,
    scenario.facts,
    tasks,
    fetchers,
    itemsConfigOverride,
    isOnboarding
  ]);
  return (
    <div className="scenario-page">
      <div className="scenario-page-header">
        <div className="scenario-page-header-right">
          <Button
            icon={<Icon name={"arrow-left"} />}
            style={{ marginRight: 16 }}
            onClick={onBack}
          />
          <Input
            value={title}
            variant="borderless"
            onChange={updateScenarioTitle}
            className="scenario-page-name"
            disabled={isSurveyTask}
            style={{
              width: titleInputWidth,
            }}
          />
          <span ref={titleMeasureRef} className="scenario-page-name-measure">
            {title || " "}
          </span>
          <div style={{ marginRight: 10 }}>
            <UserActivityStatus
              userStatus={
                previewActive
                  ? 'active'
                  : (showChangesButtons ? 'pending' : (scenario.status || 'inactive'))
              }
              fontSize={14}
            />
          </div>
          {authors && authors.length > 0 && (
            <>
              <button
                type="button"
                className="scenario-page-edit-btn"
                ref={scenarioEditButtonRef}
                onClick={openActivityModal}
                aria-label="Scenario settings"
              >
                ⋮
              </button>
              <ScenarioEditModal
                isOpen={isOpenActivityModal}
                onCancel={closeActivityModal}
                scenarioEditButtonRef={scenarioEditButtonRef}
                authors={authors}
                scenario={scenario}
              />
            </>
          )}
          {isOnboarding && (
            <TagsSelect
              defaultValue={[
                ...cohorts,
                ...normalizeGroups(scenario.GroupOnboardingSurveys),
              ].filter((cohort) => scenarioCohortsIds.includes(cohort.id))}
              options={[
                ...cohorts,
                ...normalizeGroups(scenario.GroupOnboardingSurveys),
              ]}
              onChange={updateScenarioCohorts}
              selectPlaceholder="Select cohort"
              readonly={previewActive}
            />
          )}
        </div>
        <div className="scenario-page-header-left">
          {showButtonsLoader && (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
          )}
          <Button
            ref={playBtnRef}
            className="scenario-page-header-left-btn scenario-page-run-btn"
            onClick={handleToggleSimulator}
            icon={<Icon name={showSimulator ? "stop" : "run"} />}
            disabled={!previewActive && !isDraftValid && !showSimulator}
            data-testid="scenario-play"
          >
            {showSimulator ? "Stop" : "Play"}
          </Button>
          {previewActive ? (
            <Button
              type="primary"
              onClick={handleReturnToDraft}
              className="scenario-page-header-left-btn"
            >
              {t("trails.return_to_draft")}
            </Button>
          ) : showChangesButtons ? (
            <div className="scenario-page-changes-actions">
              <Button
                onClick={openDiscardModal}
                className="scenario-page-header-left-btn"
                disabled={isCheckingChanges}
                loading={isCheckingChanges}
              >
                {t("scenario_management.discard_changes")}
              </Button>

              <Button
                onClick={openChangeLogsModal}
                className="plan-page-header-left-btn"
                loading={isCheckingChanges || isChangesLoading}
                disabled={isCheckingChanges || isChangesLoading}
              >
                {t("scenario_management.view_changes")}
                {!isChangesLoading && logsForScenario.length > 0 ? ` (${logsForScenario.length})` : null}
              </Button>

              <Button
                type="primary"
                onClick={activateScenario}
                className="scenario-page-header-left-btn"
                disabled={!isDraftValid || isCheckingChanges || isChangesLoading}
                loading={isCheckingChanges}
                data-testid="scenario-activate"
              >
                {scenario?.status === "active" || scenario?.status === "pending"
                  ? t("scenario_management.save")
                  : t("scenario_management.activate")}
              </Button>
            </div>
          ) : null}
          <ChangeLogsModalComponent
            isModalOpen={isOpenChangeLogsModal}
            handleCancel={handleChangeLogsCancel}
            title={"Changes"}
            logs={logsForScenario}
            onPreviewActive={handlePreviewActive}
          />
          <DiscardModalComponent
            isModalOpen={isOpenDiscardModal}
            handleOk={handleDiscardOk}
            handleCancel={handleDiscardCancel}
            title={"Discard changes"}
          />
        </div>
      </div>
      <UnavailableArticlesNotice graph={editorData} />
      <div className="scenario-editor">
        {scenarioEditor}
      </div>
      <QrFlyout
        isOpen={showSimulator}
        anchorRef={playBtnRef}
        onCancel={() => setShowSimulator(false)}
      >
        <ScenarioSimulatorQr
          itemType={isSurveyTask ? 'surveyTask' : isOnboarding ? 'onboarding' : 'scenario'}
          scenarioVersionId={previewActive ? activeVersion?.id : scenario.ScenarioVersion?.id}
          scenarioId={isSurveyTask ? scenario.ScenarioVersion?.dailyTaskId : scenario.ScenarioVersion?.scenarioId}
        />
      </QrFlyout>
    </div>
  );
};

export default ScenarioPage;
